import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

function Login() {
  const [activeTab, setActiveTab] = useState(0);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [clientToken, setClientToken] = useState('');
  const [userToken, setUserToken] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { loginAsAdmin, loginWithClientToken, loginWithUserToken } = useAuth();
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useCustomTheme();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
  };

  const handleOpenTerms = () => {
    setTermsDialogOpen(true);
  };

  const handleCloseTerms = () => {
    setTermsDialogOpen(false);
  };

  const handleAgreeToTerms = () => {
    setAgreedToTerms(true);
    setTermsDialogOpen(false);
    setError('');
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    setLoading(true);
    
    try {
      const success = await loginAsAdmin(username, password);
      if (success) {
        navigate('/');
      } else {
        setError('Invalid admin credentials. Please check your username and password.');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response) {
        setError(err.response.data?.detail || 'Authentication failed');
      } else if (err.request) {
        setError('No response from server. Please check if the API server is running.');
      } else {
        setError('An error occurred during login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClientTokenLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

        
    if (!agreedToTerms) {
      setError('Пожалуйста, согласитесь с условиями пользовательского соглашения перед входом в систему.');
      setTermsDialogOpen(true);
      setLoading(false);
      return;
    }
    
    try {
      const success = await loginWithClientToken(clientToken);
      if (success) {
        navigate('/');
      } else {
        setError('Invalid client token. Please check your token.');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response) {
        setError(err.response.data?.detail || 'Authentication failed');
      } else if (err.request) {
        setError('No response from server. Please check if the API server is running.');
      } else {
        setError('An error occurred during login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUserLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    
    
    setLoading(true);
    
    try {
      const success = await loginWithUserToken(userToken);
      if (success) {
        // Enter fullscreen mode when user login is successful
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch(err => {
            console.log('Fullscreen request failed:', err);
          });
        }
        navigate('/user');
      } else {
        setError('Invalid client token. Please check your token.');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response) {
        setError(err.response.data?.detail || 'Authentication failed');
      } else if (err.request) {
        setError('No response from server. Please check if the API server is running.');
      } else {
        setError('An error occurred during login');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.default'
    }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            UFO Planet Admin
          </Typography>
          <Tooltip title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`} arrow>
            <IconButton
              onClick={toggleTheme}
              sx={{ 
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Container component="main" maxWidth="xs" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography component="h1" variant="h5" gutterBottom>
              UFO Planet Admin
            </Typography>
            
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="Admin Login" />
              <Tab label="Client Token" />
              <Tab label="User Login" />
            </Tabs>

            {error && (
              <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                {error}
              </Alert>
            )}

            {activeTab === 0 ? (
              <Box component="form" onSubmit={handleAdminLogin} sx={{ mt: 1, width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In as Admin'}
                </Button>
              </Box>
            ) : activeTab === 1 ? (
              <Box component="form" onSubmit={handleClientTokenLogin} sx={{ mt: 1, width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="clientToken"
                  label="Client Token"
                  name="clientToken"
                  autoFocus
                  value={clientToken}
                  onChange={(e) => setClientToken(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In with Token'}
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleUserLogin} sx={{ mt: 1, width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="userToken"
                  label="Client Token"
                  name="userToken"
                  autoFocus
                  value={userToken}
                  onChange={(e) => setUserToken(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In as User'}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 3,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © 2024 Perkflow tech
        </Typography>
        <Link
          component="button"
          variant="body2"
          onClick={handleOpenTerms}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          Пользовательское соглашение
        </Link>
      </Box>

      {/* Terms of Service Dialog */}
      <Dialog
        open={termsDialogOpen}
        onClose={handleCloseTerms}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle>
          Публичная оферта
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ whiteSpace: 'pre-line' }}>
            <Typography variant="h6" gutterBottom>
              Публичная оферта
            </Typography>
            <Typography variant="body2" paragraph>
              Индивидуального предпринимателя Реверчука Захара Игоревича
              ОГРНИП: 324180000055050, ИНН: 183113272251
            </Typography>
            <Typography variant="body2" paragraph>
              Настоящий документ является официальным предложением (публичной офертой) ИП Реверчук Захар Игоревич, в дальнейшем именуемого "Исполнитель", любому физическому лицу, в дальнейшем именуемому "Пользователь", заключить договор на условиях, изложенных ниже.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              1. Предмет соглашения
            </Typography>
            <Typography variant="body2" paragraph>
              1.1. Исполнитель предоставляет Пользователю доступ к цифровым токенам ("токены") на платформе Perkflow.
            </Typography>
            <Typography variant="body2" paragraph>
              1.2. Токены могут быть использованы Пользователем исключительно в рамках платформы для доступа к функциональности генерации уникальных изображений, элементов игрового мира и других персонализированных функций.
            </Typography>
            <Typography variant="body2" paragraph>
              1.3. Оплата токенов осуществляется на основании настоящего соглашения, через интерфейс сайта.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              2. Условия оплаты
            </Typography>
            <Typography variant="body2" paragraph>
              2.1. Стоимость токенов указывается на сайте в момент покупки. Оплата производится в рублях или иной доступной валюте с использованием платёжных средств, указанных на сайте.
            </Typography>
            <Typography variant="body2" paragraph>
              2.2. После подтверждения оплаты токены зачисляются на внутренний баланс Пользователя.
            </Typography>
            <Typography variant="body2" paragraph>
              2.3. Оплата считается подтверждённой после поступления средств на расчётный счёт Исполнителя.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              3. Возврат средств
            </Typography>
            <Typography variant="body2" paragraph>
              3.1. Возврат средств возможен только в случае технической ошибки, в результате которой токены не были зачислены Пользователю.
            </Typography>
            <Typography variant="body2" paragraph>
              3.2. Возврат осуществляется на основании письменного обращения Пользователя с приложением подтверждающих документов. Срок рассмотрения заявки — до 10 рабочих дней.
            </Typography>
            <Typography variant="body2" paragraph>
              3.3. Частичный или полный возврат оплаченных токенов по инициативе Пользователя не предусмотрен.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              4. Использование токенов
            </Typography>
            <Typography variant="body2" paragraph>
              4.1. Токены являются цифровыми единицами, не имеющими номинальной стоимости вне платформы Perkflow и не подлежат обмену на денежные средства.
            </Typography>
            <Typography variant="body2" paragraph>
              4.2. Токены не подлежат передаче третьим лицам.
            </Typography>
            <Typography variant="body2" paragraph>
              4.3. Срок действия токенов — не ограничен, за исключением случаев прекращения работы сервиса. В случае закрытия сервиса Пользователям будет предложено использовать токены в течение 30 дней.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5. Ответственность
            </Typography>
            <Typography variant="body2" paragraph>
              5.1. Исполнитель не несёт ответственности за невозможность использования токенов по причинам, не зависящим от него (например, технические сбои на стороне Пользователя, ограничение доступа к интернету и т.п.).
            </Typography>
            <Typography variant="body2" paragraph>
              5.2. Исполнитель вправе вносить изменения в функциональность, правила использования токенов и условия настоящей оферты, размещая обновления на сайте.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              6. Принятие оферты
            </Typography>
            <Typography variant="body2" paragraph>
              6.1. Совершение Пользователем оплаты токенов является полным и безоговорочным принятием условий настоящей оферты.
            </Typography>
            <Typography variant="body2" paragraph>
              6.2. С момента оплаты токенов договор между Пользователем и Исполнителем считается заключённым.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              7. Реквизиты исполнителя:
            </Typography>
            <Typography variant="body2" paragraph>
              ИП Реверчук Захар Игоревич
              ОГРНИП: 324180000055050
              ИНН: 183113272251
              Электронная почта: zakhar@reverch.uk
              Юридический адрес: Москва, Мосфильмовская улица, д. 40
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTerms}>
            Закрыть
          </Button>
          <Button onClick={handleAgreeToTerms} variant="contained" color="primary">
            Я согласен с условиями оферты
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login; 